import type { TlBlock } from '../spec/types'

/**
 * A step timeline — what happened, when, and what is still ahead.
 *
 * Verbatim from `docs/design/reference/blocks/tl.html`. Each step is a toned
 * 5px bar over a state chip, a label, a monospace timestamp and a note, laid
 * out left-to-right and scrolled rather than wrapped: the order IS the content
 * here, and a step that wraps onto a second line stops reading as "after".
 *
 * The corpus uses this for three things that all share one shape — a Didit
 * session's progress, a dunning ladder, and the routing resolution order — and
 * in each the same discipline applies:
 *
 *   - **Future steps are shown, dimmed, never hidden.** `data-dim` is the
 *     scheduled-but-not-yet step: `re-verification 2027-11-05`, `Restricted
 *     (receive-only) 2026-08-01`. An operator has to be able to see what the
 *     system will do next and on what date, because that is the thing a
 *     customer is about to phone about.
 *   - **The state text is the underlying state, not a friendlier synonym.**
 *     The identity timeline runs `approved` then `accepted_by_emi` as two
 *     separate steps precisely because they are two separate claims: `approved`
 *     is our IDV result, acceptance is FinXP's, and FinXP's is the one that
 *     counts (invariant 2). Collapsing them into "verified" would erase the
 *     distinction the block exists to draw.
 *
 * The tone lives on the bar as well as the chip, so the whole sequence reads as
 * a severity ramp before a single word of it is read.
 */
export function Tl({ block }: { block: TlBlock }) {
  return (
    <div style={{ border: '1px solid var(--k-line)', background: 'var(--k-panel)', padding: '12px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 0,
          overflowX: 'auto',
          paddingBottom: '4px',
        }}
      >
        {block.steps.map((step, i) => (
          <div
            // Steps are positional in the design corpus; a timeline is ordered
            // by definition and carries no step id to key on.
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            data-dim={step[5] === true ? '1' : ''}
            style={{ flex: 1, minWidth: '150px', padding: '0 12px 0 0' }}
          >
            <div data-bar={step[3]} style={{ height: '5px', width: '100%' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '7px' }}>
              <span
                data-tone={step[3]}
                style={{
                  padding: '0 5px',
                  fontSize: '9.5px',
                  fontWeight: 800,
                  letterSpacing: '.05em',
                  textTransform: 'uppercase',
                }}
              >
                {step[2]}
              </span>
            </div>
            <div style={{ fontSize: '12.5px', fontWeight: 600, marginTop: '5px' }}>{step[0]}</div>
            <div
              style={{
                fontFamily: 'var(--k-mono)',
                fontSize: '11px',
                color: 'var(--k-mut)',
                marginTop: '2px',
              }}
            >
              {step[1]}
            </div>
            <div
              style={{
                fontSize: '11.5px',
                color: 'var(--k-mut)',
                marginTop: '4px',
                textWrap: 'pretty',
              }}
            >
              {step[4] ?? ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
